/**
 * Outside the window — the country's window is a hard limit (12/10/2026),
 * lifted on purpose (14/10/2026): "Not sent" keeps it (one drop may still go
 * from its row in Drop slots), "Sent" lets every drop of this container —
 * listed, optimizer extras, hit again — go outside it, planned and auto-sent.
 * A route test has no choice to make: its drops go before the window on
 * purpose, every one of them (user, 20/10/2026: "that's the point"), so the
 * row states the fact. The line under it counts today's drops on each side.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Moon, RadioTower } from 'lucide-react';

import type { DropGenPlanDetail } from '../../../../../drop-generator.types';
import { windowText } from '../../../../../utils/clock/dk-label';
import { useCountryClock } from '../../../../shared/time/country-clock-context';
import type { ContainerEditor } from '../../../state/useContainerEditor';
import { ChoiceChips } from '../../controls/ChoiceChips';
import { StepRow, ValueLine } from '../../controls/StepRow';

const OPTIONS = [
  { value: 'KEEP', label: 'Not sent' },
  { value: 'SEND', label: 'Sent' },
] as const;

const ROUTE_TEST_HINT =
  'A route test’s drops go before the country’s window opens, on purpose — every one of them, planned and auto-sent — so the day starts on the best route. With the Route tests strategy the first drop must be before the window.';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  /** Drops outside the window that do not go. */
  outside: number;
  /** Drops outside the window that go anyway (the container's or their own choice, or a route test's). */
  sentOutside: number;
  /** A route test: its drops go before the window on purpose — a fact, not a choice (20/10/2026). */
  routeTest?: boolean;
}

const drops = (n: number) => `${n} drop${n === 1 ? '' : 's'}`;

export const WindowRow: FC<Props> = ({ editor, plan, outside, sentOutside, routeTest = false }) => {
  const { draft, setField } = editor;
  const send = draft.sendOutsideWindow === true;
  const label = windowText(plan.windowStartMin, plan.windowEndMin, useCountryClock()); // Danish first (15/10/2026)
  const hint = routeTest
    ? ROUTE_TEST_HINT
    : `The country's window (${label}) is a hard limit: a drop outside it is not sent. "Sent" lets this container's drops go outside it — its listed drops, optimizer extras and hit-again drops, planned and auto-sent. One drop alone can go too, from its row in Drop slots.`;
  return (
    <StepRow icon={Moon} label="Outside the window" hint={hint}>
      {routeTest ? (
        <span className="inline-flex h-8 items-center gap-1.5 self-start rounded-md border border-primary/30 bg-primary/5 px-2.5 text-xs font-medium text-primary" data-testid="window-route-test">
          <RadioTower className="h-3.5 w-3.5" aria-hidden />
          Route test — goes before the window
        </span>
      ) : (
        <ChoiceChips options={OPTIONS} value={send ? 'SEND' : 'KEEP'} onChange={(v) => setField('sendOutsideWindow', v === 'SEND')} ariaLabel="Outside the window" />
      )}
      <ValueLine key={routeTest ? 'test' : send ? 'send' : 'keep'}>
        <span className="text-xs text-muted-foreground">{lineOf(send, label, { outside, sentOutside, routeTest })}</span>
      </ValueLine>
    </StepRow>
  );
};

function lineOf(send: boolean, label: string, n: { outside: number; sentOutside: number; routeTest: boolean }): string {
  if (n.routeTest)
    return n.sentOutside > 0
      ? `${drops(n.sentOutside)} go before the window (${label}) on purpose — a route test’s may, so the day starts on the best route`
      : `every drop is inside the window (${label}) today — a route test’s may go before it`;
  if (send) return n.sentOutside > 0 ? `${drops(n.sentOutside)} go outside the window (${label})` : `every drop is inside the window (${label}) today`;
  const kept = n.outside > 0 ? `${drops(n.outside)} outside the window (${label}) — not sent` : `every drop is inside the window (${label})`;
  return [kept, n.sentOutside > 0 ? `${drops(n.sentOutside)} sent anyway, by their own choice` : ''].filter(Boolean).join(' · ');
}
